import _ from 'underscore';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { ExtractEncryptionMode } from '../../../commonInfra/utils/sites/SitePropertyTypes';
import { ExtractEncryptionState, IDataSourceInfo } from '../../../datasource/models/IDataSourceInfo';
import { EntityActionNames, IEntityAction } from '../../../spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import {
  isAllExtractDatasource,
  isAnyDatasoursEncrypted,
  isAnyDatasoursUnencrypted,
  isAnyLiveNonFileBasedNorSqlProxyDatasource,
  isAnyPendingExtractStatus,
  isAnyVirtualConnectionDatasource,
  isDatasoursAllLive,
  isEncryptionEnabled,
  isEncryptionEnforced,
  isWorkbookEncryptedExtract,
  userCanCreateExtractOnEmbeddedDs
} from '../../../spacesPlatform/components/ActionsMenu/EntityActions/IEntityActionHelpers';
import { IExtractConvertAction } from '../../../spacesPlatform/models/actionNames';
import { IExplorableItemTypeInfo, ISpaceTypeInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { selectContentFromAppState } from '../../../spacesPlatform/selectors/ContentSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../../spacesPlatform/selectors/SpaceInfoSelectors';
import { isWorkbookSpace, SpaceInfo } from '../../../spacesPlatform/state/SpaceInfo';
import { IWorkbookInfo } from '../../../workbook/models/IWorkbookInfo';
import { createExtractsMethod } from '../../actions/RunCreateExtractsAction';


export const CreateEncryptedExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
    name: EntityActionNames.CreateExtractsEmbEncrypted,
    translateName: Messages.createExtractsForDsSubActionEncrypted,
    isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
      return isVisibleForFlyoutButton(state, targetItems);
    },
    isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return true;
    },
    isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return false;
    },
    createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.encrypted)
};

export const CreateUnencryptedExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtractsEmbUnencrypted,
  translateName: Messages.createExtractsForDsSubActionUnencrypted,
  isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    return isVisibleForFlyoutButton(state, targetItems);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return true;
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return false;
  },
  createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.unencrypted)
};

export const CreateExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtractsEmb,
  translateName: (targetItems: IDataSourceInfo[], state: AppState) => {
    if (showEncryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionEncrypted();
    } else if (showUnencryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionUnencrypted();
    } else {
      return Messages.createExtractForDsAction();
    }
  },
  isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    const spaceInfo: SpaceInfo = selectSpaceInfo(state);
    return isWorkbookSpace(spaceInfo);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return userCanCreateExtractOnEmbeddedDs(state) && isClickableForExtractButton(state, targetItems);
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return isCheckedForExtractButton(state, targetItems);
  },
  createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, SetExtractEncryptionType(getAppStore().getState()))
};

function SetExtractEncryptionType(state: AppState) {
  const workbook: ISpaceTypeInfo  = selectSpaceInfoItem(state);
  return (SessionSiteSelectors.extractEncryptionMode(getAppStore().getState()) === ExtractEncryptionMode.ENFORCED || isWorkbookEncryptedExtract(workbook as IWorkbookInfo))  ?
  ExtractEncryptionState.encrypted : ExtractEncryptionState.unencrypted;
}

function isVisibleForFlyoutButton(state: AppState, targetItems: IDataSourceInfo[]) {
  const spaceInfo: SpaceInfo = selectSpaceInfo(state);
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  // if workbook has embedded datasources are live, will show flyout button
  return isWorkbookSpace(spaceInfo) && isEncryptionEnabled(state) && isDatasoursAllLive(_.map(datasources, ds =>ds as IDataSourceInfo));
}


function  isClickableForExtractButton(state: AppState, targetItems: IDataSourceInfo[]) {
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  // case: 1) No pending status
  //       2) select any live and non-file nor non-sqlproxy based datasource, if it's not a virtual connection
  return !isAnyPendingExtractStatus(_.map(datasources, ds =>ds as IDataSourceInfo)) &&
  isAnyLiveNonFileBasedNorSqlProxyDatasource(targetItems) && !isAnyVirtualConnectionDatasource(targetItems);
}

function isCheckedForExtractButton(state: AppState, targetItems: IDataSourceInfo[]) {
  // case: 1) select all/single extract based embedded datasource
  return isAllExtractDatasource(targetItems);
}

function showUnencryptedButtonName(state: AppState, targetItems: IDataSourceInfo[]) {
  // case Encryption enable workbook has Unencrypted extract datasource
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  return  (isEncryptionEnabled(state) && isAnyDatasoursUnencrypted(_.map(datasources, ds =>ds as IDataSourceInfo)));
}

function showEncryptedButtonName(state: AppState, targetItems: IDataSourceInfo[]) {
  // case 1) Encryption Forced
  //      2) Encryption enable workbook has Encrypted extract datasource
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  return isEncryptionEnforced(state) ||
  (isEncryptionEnabled(state) && isAnyDatasoursEncrypted(_.map(datasources, ds =>ds as IDataSourceInfo)));
}
